import { API_BASE_URL } from '../config';
import { getToken, getRefreshToken, setSession, clearSession } from '../auth';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when a request is 401 and cannot be refreshed — route to login. */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Session expired') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

function buildQuery(params: object): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// One in-flight refresh shared across concurrent 401s.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/v2/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const pair = (await res.json()) as {
          accessToken?: string;
          refreshToken?: string;
          expiresIn?: number;
        };
        if (!pair.accessToken || !pair.refreshToken) return false;
        setSession({
          accessToken: pair.accessToken,
          refreshToken: pair.refreshToken,
          expiresIn: pair.expiresIn ?? 900,
        });
        return true;
      } catch {
        return false;
      }
    })();
  }
  const ok = await refreshInFlight;
  refreshInFlight = null;
  return ok;
}

async function request<T>(
  method: Method,
  path: string,
  opts: { params?: object; body?: unknown } = {},
  retried = false,
): Promise<T> {
  const token = getToken();
  const hasBody = opts.body !== undefined;
  const res = await fetch(`${API_BASE_URL}${path}${buildQuery(opts.params ?? {})}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(hasBody ? { body: JSON.stringify(opts.body) } : {}),
  });

  if (res.status === 401) {
    if (!retried && (await tryRefresh())) {
      return request<T>(method, path, opts, true);
    }
    clearSession();
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.message) {
        message = Array.isArray(body.message)
          ? body.message.join(', ')
          : String(body.message);
      }
    } catch {
      /* non-JSON error body — keep the default message */
    }
    throw new ApiError(res.status, message);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const apiGet = <T>(path: string, params: object = {}): Promise<T> =>
  request<T>('GET', path, { params });
export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>('POST', path, { body: body ?? {} });
export const apiPatch = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>('PATCH', path, { body: body ?? {} });
export const apiDelete = <T>(path: string): Promise<T> =>
  request<T>('DELETE', path);
